@if (showTooltip) {
  <app-map-tooltip
    style="position: relative"
    id="tooltip"
    [screenPoint]="screenPoint"
    [tooltipContent]="tooltipContent"
    />
}

<!-- Picture hover preview (shows on marker hover) -->
@if (showPictureHoverPreview && pictureHoverPreviewData.length > 0) {
  <app-picture-hover-preview
    [pictures]="pictureHoverPreviewData"
    [screenPoint]="pictureHoverScreenPoint"
    />
}

<!-- Item popup (bid items, activities, deliveries) -->
@if (showItemPopup) {
  <map-popup-wrapper
    style="position: relative"
    id="itemPopup"
    [popupContent]="itemPopupContent"
    [stationLine]="itemPopupStationLine"
    [screenPoint]="itemPopupScreenPoint"
    [showPopup]="showItemPopup"
    (showPopupChange)="onBidItemPopupChange($event)"
    (seeMore)="onBidItemSeeMore($event)"
    />
}

<!-- Warranty claim hover/click popup -->
@if (showClaimPopup && claimPopupData) {
  <div
    class="claim-popup-host"
    [style.left.px]="claimPopupScreenPoint.x + 14"
    [style.top.px]="claimPopupScreenPoint.y + 14"
    (mouseenter)="onClaimPopupMouseEnter()"
    (mouseleave)="onClaimPopupMouseLeave()">
    <app-claim-map-popup
      [data]="claimPopupData"
      [claims]="claimPopupClaims"
      (claimSelected)="onClaimPopupSelect($event)" />
  </div>
}

<!-- Warranty issue hover/click popup -->
@if (showIssuePopup && issuePopupData) {
  <div
    class="claim-popup-host"
    [style.left.px]="issuePopupScreenPoint.x + 14"
    [style.top.px]="issuePopupScreenPoint.y + 14"
    (mouseenter)="onIssuePopupMouseEnter()"
    (mouseleave)="onIssuePopupMouseLeave()">
    <app-issue-map-popup
      [data]="issuePopupData"
      [issues]="issuePopupIssues"
      (issueSelected)="onIssuePopupSelect($event)" />
  </div>
}

@if (referenceInspectionOpen) {
  <!-- Same panel as the layer drawer's preview (Figma map side panel 33128:76593): several
       features under the click start as a list; a row, or a single hit, opens its details. -->
  <section class="reference-inspection" [class.reference-inspection-fullscreen]="isFullScreen" data-testid="reference-feature-inspection"
    aria-label="Reference feature details" tabindex="0" [attr.aria-busy]="referenceInspectionLoading"
    (keydown.escape)="closeReferenceInspection()" (mouseleave)="hoverReferenceCandidate(null)">
    <div class="reference-inspection-header">
      @if (referenceFeatureView === 'details' && referenceCandidates.length > 1) {
        <button type="button" class="reference-inspection-back" aria-label="Back to the list" data-testid="reference-feature-back"
          (click)="backToReferenceCandidates()"><mat-icon svgIcon="chevron-left"></mat-icon></button>
      }
      <h3 class="reference-inspection-title" [matTooltip]="referenceFeatureView === 'details' ? selectedReferenceCandidate?.label : null"
        matTooltipClass="project-name-tooltip">
        {{ referenceInspectionLoading ? 'Looking up…' : (referenceFeatureView === 'details' ? selectedReferenceCandidate?.label : 'Features here') }}
      </h3>
      <button type="button" class="reference-inspection-close" aria-label="Close reference details" (click)="closeReferenceInspection()">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M15 5L5 15M5 5L15 15" stroke="#060B1E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
    @if (referenceStationLine) {
      <!-- Spec §3a: the clicked point's station (not the feature's centre) -->
      <p class="reference-station-line" data-testid="reference-feature-station">{{ referenceStationLine }}</p>
    }
    @if (referenceInspectionMessage) {
      <p class="text-type-14 reference-inspection-secondary" role="status">{{ referenceInspectionMessage }}</p>
    }
    @if (referenceInspectionIncomplete) {
      <p class="reference-inspection-notice text-type-14" role="status">
        Some features could not be checked. Zoom in or hide overlapping layers for a complete result.
      </p>
    }
    @if (referenceFeatureView === 'list' && referenceCandidates.length > 1) {
      <ul class="reference-inspection-list" aria-label="Nearby reference features">
        @for (candidate of referenceCandidates; track candidate.layer.id + ':' + candidate.featureId) {
          <li>
            <button type="button" class="reference-inspection-row" data-testid="reference-feature-row" (click)="selectReferenceCandidate(candidate)"
              (mouseenter)="hoverReferenceCandidate(candidate)" (focus)="hoverReferenceCandidate(candidate)">
              <span class="reference-inspection-row-text">
                <span class="text-type-14 reference-inspection-row-label" [matTooltip]="candidate.label" matTooltipClass="project-name-tooltip" matTooltipShowDelay="400">{{ candidate.label }}</span>
                @if (candidate.meta) { <span class="reference-inspection-secondary reference-inspection-row-sub">{{ candidate.meta }}</span> }
              </span>
              <mat-icon class="reference-inspection-row-caret" svgIcon="chevron-left" aria-hidden="true"></mat-icon>
            </button>
          </li>
        }
      </ul>
      @if (referenceCandidatesHidden) {
        <p class="text-type-14 reference-inspection-secondary reference-inspection-more" role="status">{{ referenceCandidatesHidden }} more nearby. Zoom in to narrow it down.</p>
      }
    }
    @if (referenceFeatureView === 'details' && selectedReferenceCandidate; as selected) {
      <div class="reference-inspection-content" data-testid="reference-feature-details">
        <h4 class="text-type-11 reference-inspection-secondary">{{ selected.layer.name }}</h4>
        <dl class="reference-inspection-fields text-type-14">
          @for (field of selected.fields; track $index) {
            <div><dt>{{ field.label }}</dt><dd>{{ field.value }}</dd></div>
          }
        </dl>
        @if (!selected.fields.length) {
          <p class="text-type-14 reference-inspection-secondary">No details recorded for this feature.</p>
        }
        <dl class="reference-inspection-fields reference-inspection-provenance text-type-14">
          <div><dt>Source captured</dt><dd>{{ selected.layer.sourceCapturedAt | date:'medium' }}</dd></div>
          <div><dt>Attribution</dt><dd>{{ selected.layer.attribution || 'Not provided in this publication' }}</dd></div>
          <div><dt>Source layer</dt><dd>{{ selected.layer.sublayerKey }}</dd></div>
          <div><dt>Source version</dt><dd>{{ selected.layer.sourceVersionId }}</dd></div>
          <div><dt>Feature</dt><dd>{{ selected.featureId }}</dd></div>
        </dl>
        @if (!capturedHereHidden) {
          <section class="captured-here" data-testid="captured-here-section" aria-label="Records captured at this feature"
            [attr.aria-busy]="capturedHereLoading">
            <h4 class="text-type-11 reference-inspection-secondary">Captured here · {{ capturedHereCount }}</h4>
            @if (capturedHereError) {
              <p class="text-type-14 reference-inspection-secondary" role="status">{{ capturedHereError }}</p>
            } @else if (!capturedHereLoading && !capturedHereItems.length) {
              <p class="text-type-14 reference-inspection-secondary">Nothing linked to this feature yet.</p>
            }
            @if (capturedHereItems.length) {
              <ul class="captured-here-list">
                @for (item of capturedHereItems; track item.id) {
                  <li class="captured-here-row" data-testid="captured-here-row">
                    <span class="text-type-14 captured-here-label" [matTooltip]="item.title" matTooltipShowDelay="400">{{ item.title }}</span>
                    <span class="text-type-13 reference-inspection-secondary">{{ item.type }}@if (item.date) { · {{ item.date | date:'mediumDate' }}}</span>
                    @if (item.detail) {
                      <span class="text-type-13 reference-inspection-secondary">{{ item.detail }}</span>
                    }
                  </li>
                }
              </ul>
              @if (capturedHereMore) {
                <p class="text-type-13 reference-inspection-secondary">{{ capturedHereMore }}</p>
              }
            }
          </section>
        }
      </div>
      @if (canCreateReferenceIssue) {
        <button type="button" class="reference-inspection-create text-type-11" data-testid="create-issue-at-reference"
          (click)="createIssueAtReference()">Create issue here</button>
      }
    }
  </section>
}

<!-- §3c: the active alignment chip; moved into the map's bottom-left corner beside the station readout -->
<div #stationingChipHost class="stationing-chip-host" [hidden]="!stationingChipShown">
  <button type="button" class="stationing-chip" data-testid="map-stationing-alignment" [matMenuTriggerFor]="stationingAlignmentMenu"
    [attr.aria-label]="'Stationing alignment: ' + (stationingAlignmentLocked ? stationingAlignmentName : 'Automatic, ' + stationingAlignmentName)"
    [matTooltip]="stationingAlignmentLocked ? 'Stations read on ' + stationingAlignmentName : 'Automatic: stations read on the nearest alignment'">
    {{ stationingAlignmentName }}<span class="stationing-chip-caret" aria-hidden="true">▾</span>
  </button>
  <mat-menu #stationingAlignmentMenu="matMenu" class="tab-more-menu">
    <button mat-menu-item type="button" data-testid="map-stationing-alignment-auto" [attr.aria-checked]="!stationingAlignmentLocked" role="menuitemradio"
      (click)="chooseStationingAlignment(null)">@if (!stationingAlignmentLocked) {<mat-icon aria-hidden="true">check</mat-icon>}Automatic (nearest)</button>
    @for (name of stationingAlignmentNames; track name) {
      <button mat-menu-item type="button" data-testid="map-stationing-alignment-option" role="menuitemradio"
        [attr.aria-checked]="stationingAlignmentLocked && name === stationingAlignmentName" (click)="chooseStationingAlignment(name)">@if (stationingAlignmentLocked && name === stationingAlignmentName) {<mat-icon aria-hidden="true">check</mat-icon>}{{ name }}</button>
    }
  </mat-menu>
</div>

<app-esri-map
  #projectMap
  id="projectMap"
  class="project-map"
  [allowMarkerClick]="true"
  [fullscreenElement]="fullscreenElement"
  (markerClick)="onMarkerClick($event)"
  (markerHover)="_onShowTooltip($event)"
  (mapLoaded)="onMapLoaded($event)"
  (mapComponentLoaded)="onMapComponentLoaded($event)"
  (viewLoaded)="onViewLoaded($event)"
  (fullscreenChange)="onFullscreenChange($event)"
  />

<!-- No-location items badge (bid items + activities + deliveries + form groups) -->
<!-- Show in fullscreen OR in single day mode (non-fullscreen). formNoLocationCount
     is only > 0 when a form group is selected, so it gates the forms case the same
     way layerSettings.* gates the base types. -->
@if ((isFullScreen || isSingleDayMode) && getTotalNoLocationCount() > 0 && (layerSettings.bidItem || layerSettings.activity || layerSettings.delivery || formNoLocationCount > 0) && !selectedItemData) {
  <app-no-location-badge
    [type]="'items'"
    [count]="getTotalNoLocationCount()"
    [isFullScreen]="isFullScreen"
    (badgeClick)="onNoLocationBadgeClick($event)"
    />
}

<!-- Issues tab: the filtered issues with no location (for-map meta), listed on click -->
@if (issuesNoLocationCount > 0) {
  <app-no-location-badge
    [type]="'items'"
    [count]="issuesNoLocationCount"
    [isFullScreen]="isFullScreen"
    [clickable]="isFullScreen"
    [attr.title]="issuesNoLocationLabel"
    [attr.aria-label]="issuesNoLocationLabel"
    data-testid="issues-no-location-badge"
    (badgeClick)="isFullScreen && onIssuesNoLocationBadgeClick($event)"
    />
}

<!-- Main mode: No-location pictures badge (circular thumbnail like drilldown) -->
<!-- Show in fullscreen OR in single day mode (non-fullscreen) -->
@if ((isFullScreen || isSingleDayMode) && !selectedItemData && mainNoLocationPicturesCount > 0 && layerSettings.picture) {
  <app-no-location-badge
    [type]="'pictures'"
    [count]="mainNoLocationPicturesCount"
    [thumbnailUrl]="mainNoLocationPictures[0]?.thumb_url || mainNoLocationPictures[0]?.original_url"
    [hasItemsBadge]="getTotalNoLocationCount() > 0 && (layerSettings.bidItem || layerSettings.activity || layerSettings.delivery)"
    [isFullScreen]="isFullScreen"
    (badgeClick)="onMainNoLocationPicturesClick($event)"
    />
}

<!-- Drilldown mode: No-location pictures badge -->
@if (selectedItemData && drilldownNoLocationCount > 0) {
  <app-no-location-badge
    [type]="'pictures'"
    [count]="drilldownNoLocationCount"
    [thumbnailUrl]="drilldownNoLocationPictures[0]?.thumb_url || drilldownNoLocationPictures[0]?.original_url"
    [isFullScreen]="isFullScreen"
    (badgeClick)="onDrilldownNoLocationPicturesClick($event)"
    />
}

<!-- Map controls (basemaps + zoom + layers + type) - fullscreen, or embedded viewer (Form 266 map pane) -->
@if (isFullScreen || showEmbeddedControls) {
  <app-map-controls
    [organizationBasemaps]="organizationBasemaps"
    [basemapId]="basemapId"
    [mapScreenshotUrl]="mapScreenshotUrl"
    [selectedLayers]="{ siteBoundary: layerSettings.siteBoundary, picture: layerSettings.picture, asBuilt: layerSettings.asBuilt }"
    [selectedTypeFilters]="getSelectedTypeFilters()"
    [availableFormGroups]="availableFormGroups"
    [projectLayers]="toolbarState"
    [canManageLayers]="canManageProjectLayers"
    [projectLayersInSidenav]="projectLayersInSidenav"
    (projectLayerToggle)="onToolbarOverlayToggle($event)"
    (projectLayerOpacity)="onOverlayOpacityChange($event)"
    (projectLayerLabels)="onOverlayLabelsToggle($event)"
    (projectLayerZoom)="zoomToOverlayIfOffscreen($event)"
    (projectLayersReset)="resetProjectLayerView()"
    (projectLayersRetry)="refreshOverlays()"
    (projectLayerSwipe)="onToolbarSwipePresetChange($event)"
    (addProjectLayer)="onAddOverlayClick()"
    (basemapChanged)="onBasemapChanged($event)"
    (zoomIn)="onZoomIn()"
    (zoomOut)="onZoomOut()"
    (layerChange)="onLayerChange($event)"
    (typeFilterChange)="onTypeFilterChange($event)"
    />
}

<!-- Map advanced widgets toolbar - only in fullscreen mode -->
@if (isFullScreen) {
  <app-map-widgets-toolbar
    [state]="toolbarState"
    (renderModeChange)="onToolbarRenderModeChange($event)"
    (swipePresetChange)="onToolbarSwipePresetChange($event)"
    (spatialSelectToggle)="onToolbarSpatialSelectToggle($event)"
    (overlayToggle)="onToolbarOverlayToggle($event)"
    (addOverlayClick)="onAddOverlayClick()">
  </app-map-widgets-toolbar>
}
